(() => {
  class NfcWholesaleConfigurator extends HTMLElement {
    constructor() {
      super();
      this.onOptionClick = this.onOptionClick.bind(this);
      this.onInputChange = this.onInputChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onTrialSubmit = this.onTrialSubmit.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.product = JSON.parse(this.querySelector('[data-product-json]').textContent);
      this.productOptions = this.product.options || [];
      this.variants = this.product.variants || [];
      this.currency = this.dataset.currency || 'MXN';
      this.locale = document.documentElement.lang || 'es-MX';
      this.showSavings = this.dataset.showSavings === 'true';
      this.resalePrice = Math.max(0, Number(this.dataset.resalePrice) || 0);
      this.defaultSelection = {
        quantity: this.dataset.defaultQuantity,
        material: this.dataset.defaultMaterial,
        technology: this.dataset.defaultTechnology,
        plateDesign: this.dataset.defaultPlateDesign,
        personalization: this.dataset.defaultPersonalization,
      };
      this.optionPositions = this.getOptionPositions();

      this.variantMap = new Map();
      this.variants.forEach((variant) => {
        this.variantMap.set(
          this.variantKey(
            this.variantOptionValue(variant, 'quantity'),
            this.variantOptionValue(variant, 'material'),
            this.variantOptionValue(variant, 'technology')
          ),
          variant
        );
      });
      this.standardDesignMaterial = this.getStandardDesignMaterial();

      this.state = this.getInitialState();
      this.buttons = Array.from(this.querySelectorAll('[data-nfc-option]'));
      this.addButton = this.querySelector('[data-nfc-add]');
      this.trialBubble = this.querySelector('[data-nfc-trial]');
      this.trialButton = this.querySelector('[data-nfc-trial-add]');
      this.trialStatusEl = this.querySelector('[data-nfc-trial-status]');
      this.statusEl = this.querySelector('[data-nfc-status]');
      this.errorEl = this.querySelector('[data-nfc-error]');
      this.whiteLabelPanel = this.querySelector('[data-nfc-white-label-panel]');
      this.standardDesignPanel = this.querySelector('[data-nfc-standard-design-panel]');
      this.materialLockNote = this.querySelector('[data-nfc-material-lock-note]');
      this.designQuantityInputs = Array.from(this.querySelectorAll('[data-nfc-design-qty]'));
      this.designTotalEl = this.querySelector('[data-nfc-design-total]');
      this.summaryEls = {
        quantity: this.querySelector('[data-nfc-summary-quantity]'),
        material: this.querySelector('[data-nfc-summary-material]'),
        plateDesign: this.querySelector('[data-nfc-summary-plate-design]'),
        plateDesignRow: this.querySelector('[data-nfc-summary-plate-design-row]'),
        technology: this.querySelector('[data-nfc-summary-technology]'),
        personalization: this.querySelector('[data-nfc-summary-personalization]'),
        unitPrice: this.querySelector('[data-nfc-unit-price]'),
        total: this.querySelector('[data-nfc-total]'),
        savings: this.querySelector('[data-nfc-savings]'),
        resale: this.querySelector('[data-nfc-resale]'),
        resaleCost: this.querySelector('[data-nfc-resale-cost]'),
        resalePrice: this.querySelector('[data-nfc-resale-price]'),
        potentialRevenue: this.querySelector('[data-nfc-potential-revenue]'),
        potentialProfit: this.querySelector('[data-nfc-potential-profit]'),
      };

      this.addEventListener('click', this.onOptionClick);
      this.addEventListener('input', this.onInputChange);
      this.addButton?.addEventListener('click', this.onSubmit);
      this.trialButton?.addEventListener('click', this.onTrialSubmit);
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this)) this.disconnectedCallback();
      });

      this.render();
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.removeEventListener('click', this.onOptionClick);
      this.removeEventListener('input', this.onInputChange);
      this.addButton?.removeEventListener('click', this.onSubmit);
      this.trialButton?.removeEventListener('click', this.onTrialSubmit);
      this.initialized = false;
    }

    getInitialState() {
      const variantFromUrl = this.getVariantFromUrl();
      const variant =
        variantFromUrl ||
        this.resolveVariant(
          this.defaultSelection.quantity,
          this.defaultSelection.material,
          this.defaultSelection.technology
        ) ||
        this.variants.find((item) => item.available) ||
        this.variants[0];

      return {
        quantity: this.variantOptionValue(variant, 'quantity') || this.defaultSelection.quantity,
        material: this.variantOptionValue(variant, 'material') || this.defaultSelection.material,
        technology: this.variantOptionValue(variant, 'technology') || this.defaultSelection.technology,
        plateDesign: this.defaultSelection.plateDesign,
        designQuantities: this.normalize(this.defaultSelection.personalization).includes('diseno estandar')
          ? this.defaultDesignQuantities(
              this.quantityNumber(this.variantOptionValue(variant, 'quantity') || this.defaultSelection.quantity)
            )
          : {},
        personalization: this.defaultSelection.personalization,
        loading: false,
      };
    }

    getVariantFromUrl() {
      const variantId = new URLSearchParams(window.location.search).get('variant');
      if (!variantId) return null;
      return this.variants.find((variant) => String(variant.id) === String(variantId)) || null;
    }

    normalize(value) {
      return String(value || '')
        .trim()
        .toLocaleLowerCase('es-MX')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    }

    getOptionPositions() {
      const positions = {
        quantity: 1,
        material: 2,
        technology: 3,
      };

      this.productOptions.forEach((option, index) => {
        const name = this.normalize(option.name || option);
        if (name.includes('cantidad')) positions.quantity = index + 1;
        if (name.includes('material')) positions.material = index + 1;
        if (name.includes('tecnologia')) positions.technology = index + 1;
      });

      return positions;
    }

    variantOptionValue(variant, key) {
      return variant?.[`option${this.optionPositions[key]}`];
    }

    variantKey(quantity, material, technology) {
      return [quantity, material, technology].map((value) => this.normalize(value)).join('||');
    }

    resolveVariant(quantity, material, technology) {
      return this.variantMap.get(this.variantKey(quantity, material, technology)) || null;
    }

    getStandardDesignMaterial() {
      const premiumVariant = this.variants.find((variant) =>
        this.normalize(this.variantOptionValue(variant, 'material')).includes('acrilico premium')
      );

      return this.variantOptionValue(premiumVariant, 'material') || this.defaultSelection.material;
    }

    quantityNumber(value) {
      const match = String(value || '').match(/\d+/);
      return match ? Number(match[0]) : 0;
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const formatted = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(amount);

      return `${formatted} ${this.currency}`;
    }

    onOptionClick(event) {
      const button = event.target.closest('[data-nfc-option]');
      if (!button || !this.contains(button) || button.disabled) return;

      const group = button.dataset.nfcGroup;
      const value = button.dataset.nfcValue;
      if (!group || !value || this.state[group] === value) return;

      this.state[group] = value;
      if (group === 'quantity' && this.isStandardDesign()) {
        this.state.designQuantities = this.defaultDesignQuantities(this.quantityNumber(value));
      }
      if (group === 'personalization') {
        if (this.normalize(value).includes('diseno estandar')) {
          this.state.material = this.standardDesignMaterial;
          this.state.designQuantities = this.defaultDesignQuantities(this.quantityNumber(this.state.quantity));
        } else {
          this.state.designQuantities = {};
        }
      }
      this.render();
    }

    onInputChange(event) {
      if (event.target.matches('[data-nfc-design-qty]')) {
        const name = event.target.dataset.nfcDesignName;
        const value = Math.max(0, parseInt(event.target.value, 10) || 0);
        this.state.designQuantities[name] = value;
        this.render();
      }
    }

    currentVariant() {
      return this.resolveVariant(this.state.quantity, this.state.material, this.state.technology);
    }

    comparableBaseVariant() {
      return this.resolveVariant('40 pz', this.state.material, this.state.technology);
    }

    isStandardDesign() {
      return this.normalize(this.state.personalization).includes('diseno estandar');
    }

    designNames() {
      return ['Placa de Google', 'Placa de Instagram', 'Placa de Facebook', 'Placa todo en uno'];
    }

    defaultDesignQuantities(total) {
      const names = this.designNames();
      const base = Math.floor((total || 0) / names.length);
      let remainder = (total || 0) % names.length;
      return names.reduce((quantities, name) => {
        quantities[name] = base + (remainder > 0 ? 1 : 0);
        remainder -= 1;
        return quantities;
      }, {});
    }

    designQuantityTotal() {
      return Object.values(this.state.designQuantities || {}).reduce((sum, value) => sum + (parseInt(value, 10) || 0), 0);
    }

    designSummary() {
      return this.designNames()
        .map((name) => {
          const quantity = this.state.designQuantities?.[name] || 0;
          return quantity > 0 ? `${quantity} ${name.replace('Placa de ', '')}` : '';
        })
        .filter(Boolean)
        .join(', ');
    }

    renderDesignQuantities(total) {
      this.designQuantityInputs.forEach((input) => {
        input.max = total || 0;
        input.value = this.state.designQuantities?.[input.dataset.nfcDesignName] || 0;
      });

      if (this.designTotalEl) {
        const selected = this.designQuantityTotal();
        this.designTotalEl.textContent = `Distribuidas ${selected} de ${total} placas.`;
      }
    }

    standardDesignError(total) {
      if (!this.isStandardDesign()) return '';
      const selected = this.designQuantityTotal();
      if (selected === total) return '';
      return selected < total
        ? `Faltan ${total - selected} placas por distribuir.`
        : `Hay ${selected - total} placas extra en la distribución.`;
    }

    render() {
      const usesStandardDesign = this.isStandardDesign();
      if (usesStandardDesign) this.state.material = this.standardDesignMaterial;

      const variant = this.currentVariant();
      const quantity = this.quantityNumber(this.state.quantity);

      this.buttons.forEach((button) => {
        const group = button.dataset.nfcGroup;
        const isSelected = this.normalize(this.state[group]) === this.normalize(button.dataset.nfcValue);
        const isLockedMaterial =
          group === 'material' &&
          usesStandardDesign &&
          this.normalize(button.dataset.nfcValue) !== this.normalize(this.standardDesignMaterial);

        button.disabled = isLockedMaterial;
        button.setAttribute('aria-disabled', String(isLockedMaterial));
        button.setAttribute('aria-pressed', String(isSelected));
      });

      const usesWhiteLabel = this.normalize(this.state.personalization).includes('white label');
      this.whiteLabelPanel?.classList.toggle('is-visible', usesWhiteLabel);
      this.whiteLabelPanel?.setAttribute('aria-hidden', String(!usesWhiteLabel));
      this.whiteLabelPanel?.toggleAttribute('inert', !usesWhiteLabel);
      this.standardDesignPanel?.classList.toggle('is-visible', usesStandardDesign);
      this.standardDesignPanel?.setAttribute('aria-hidden', String(!usesStandardDesign));
      this.standardDesignPanel?.toggleAttribute('inert', !usesStandardDesign);
      this.materialLockNote?.toggleAttribute('hidden', !usesStandardDesign);
      this.summaryEls.plateDesignRow?.toggleAttribute('hidden', !usesStandardDesign);
      this.renderDesignQuantities(quantity);

      this.summaryEls.quantity.textContent = `${quantity || this.state.quantity} placas`;
      this.summaryEls.material.textContent = this.state.material;
      this.summaryEls.plateDesign.textContent = usesStandardDesign ? this.designSummary() : '';
      this.summaryEls.technology.textContent = this.state.technology;
      this.summaryEls.personalization.textContent = this.state.personalization;

      if (!variant) {
        this.summaryEls.unitPrice.textContent = '-';
        this.summaryEls.total.textContent = '-';
        this.summaryEls.savings.textContent = '';
        this.renderResaleProjection(null, quantity);
        this.setError('Configuración no disponible');
        this.setAddDisabled(true);
        return;
      }

      this.summaryEls.total.textContent = this.formatMoney(variant.price);
      this.summaryEls.unitPrice.textContent = quantity ? `${this.formatMoney(Math.round(variant.price / quantity))} / placa` : '-';
      this.renderResaleProjection(variant, quantity);
      this.renderSavings(variant, quantity);
      const designError = this.standardDesignError(quantity);
      if (designError) {
        this.setError(designError);
        this.setAddDisabled(true, 'Revisa cantidades');
      } else {
        this.clearError();
        this.setAddDisabled(!variant.available || this.state.loading, variant.available ? '' : 'No disponible');
      }
      this.updateUrl(variant.id);
      this.dispatchConfiguratorChange(variant);
    }

    renderResaleProjection(variant, quantity) {
      if (!this.summaryEls.resale) return;

      const canProject = Boolean(variant && quantity && this.resalePrice > 0);
      this.summaryEls.resale.toggleAttribute('hidden', !canProject);
      if (!canProject) return;

      const wholesaleUnit = Math.round(variant.price / quantity);
      const potentialRevenue = this.resalePrice * quantity;
      const potentialProfit = Math.max(0, potentialRevenue - variant.price);

      this.summaryEls.resaleCost.textContent = this.formatMoney(wholesaleUnit);
      this.summaryEls.resalePrice.textContent = this.formatMoney(this.resalePrice);
      this.summaryEls.potentialRevenue.textContent = this.formatMoney(potentialRevenue);
      this.summaryEls.potentialProfit.textContent = this.formatMoney(potentialProfit);
    }

    renderSavings(variant, quantity) {
      if (!this.showSavings || !quantity) {
        this.summaryEls.savings.textContent = '';
        return;
      }

      const baseVariant = this.comparableBaseVariant();
      const baseQuantity = 40;
      if (!baseVariant || quantity <= baseQuantity) {
        this.summaryEls.savings.textContent = '';
        return;
      }

      const baseUnit = baseVariant.price / baseQuantity;
      const currentUnit = variant.price / quantity;
      const savingsPerPlate = Math.round(baseUnit - currentUnit);
      const totalSavings = Math.round((baseUnit - currentUnit) * quantity);

      this.summaryEls.savings.textContent =
        savingsPerPlate > 0 && totalSavings > 0
          ? `Ahorras ${this.formatMoney(savingsPerPlate)} por placa (${this.formatMoney(totalSavings)} en este pedido)`
          : '';
    }

    updateUrl(variantId) {
      if (!variantId || !window.history?.replaceState) return;
      const url = new URL(window.location.href);
      if (url.searchParams.get('variant') === String(variantId)) return;
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    }

    setAddDisabled(disabled, text = '') {
      if (!this.addButton) return;
      this.addButton.disabled = disabled;
      this.addButton.setAttribute('aria-disabled', String(disabled));
      const label = this.addButton.querySelector('[data-nfc-add-label]');
      if (label) label.textContent = text || (this.state.loading ? 'Preparando checkout...' : 'Comprar ahora');
    }

    setStatus(message) {
      this.statusEl.textContent = message || '';
    }

    setError(message) {
      this.errorEl.textContent = message || '';
    }

    clearError() {
      this.setError('');
    }

    buildProperties() {
      const properties = {
        Personalización: this.state.personalization,
      };

      if (this.isStandardDesign()) {
        properties['Diseño de placa'] = this.designSummary();
        this.designNames().forEach((name) => {
          properties[name] = String(this.state.designQuantities?.[name] || 0);
        });
      }

      if (this.normalize(this.state.personalization).includes('white label')) {
        properties.Personalización = 'White Label';
        properties.Logo = 'Solicitar despues de la compra';
      }

      return properties;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.state.loading) return;

      const variant = this.currentVariant();
      if (!variant) {
        this.setError('Configuración no disponible');
        return;
      }

      const designError = this.standardDesignError(this.quantityNumber(this.state.quantity));
      if (designError) {
        this.setError(designError);
        return;
      }

      if (!variant.available) {
        this.setError('Esta configuración no está disponible actualmente.');
        return;
      }

      this.state.loading = true;
      this.clearError();
      this.setStatus('');
      this.setAddDisabled(true);

      const root = window.Shopify?.routes?.root || '/';
      const addUrl = `${root}cart/add.js`;

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(variant.id),
                quantity: 1,
                properties: this.buildProperties(),
              },
            ],
          }),
        });
        const contentType = response.headers.get('content-type') || '';
        const parsedState = contentType.includes('application/json')
          ? await response.json()
          : { description: await response.text() };

        if (!response.ok || parsedState.status) {
          throw new Error(parsedState.description || parsedState.message || 'No se pudo agregar al carrito.');
        }

        this.dispatchEvent(
          new CustomEvent('taplytap:add_to_cart', {
            bubbles: true,
            detail: this.eventDetail(variant),
          })
        );

        this.setStatus('Pedido listo. Abriendo checkout...');
        window.location.href = `${root}checkout`;
        return;
      } catch (error) {
        this.setError(error.message || 'No se pudo agregar al carrito.');
      } finally {
        this.state.loading = false;
        this.setAddDisabled(!this.currentVariant()?.available);
      }
    }

    async onTrialSubmit(event) {
      event.preventDefault();
      if (!this.trialButton || this.trialButton.disabled || this.trialLoading) return;

      const variantId = this.trialButton.dataset.variantId;
      if (!variantId) return;

      const label = this.trialButton.querySelector('[data-nfc-trial-label]');

      this.trialLoading = true;
      this.trialButton.disabled = true;
      this.trialButton.setAttribute('aria-disabled', 'true');
      if (label) label.textContent = 'ABRIENDO';
      if (this.trialStatusEl) this.trialStatusEl.textContent = '';

      this.dispatchEvent(
        new CustomEvent('taplytap:add_to_cart', {
          bubbles: true,
          detail: {
            variantId: Number(variantId),
            quantity: 1,
            productTitle: this.trialButton.dataset.productTitle,
            source: 'trial-bubble',
            destination: 'checkout',
          },
        })
      );

      const root = window.Shopify?.routes?.root || '/';
      window.location.assign(`${root}cart/${encodeURIComponent(variantId)}:1`);
    }

    eventDetail(variant) {
      return {
        variantId: variant?.id || null,
        quantityTier: this.state.quantity,
        material: this.state.material,
        technology: this.state.technology,
        plateDesign: this.state.plateDesign,
        designQuantities: { ...this.state.designQuantities },
        personalization: this.state.personalization,
        price: variant?.price || null,
      };
    }

    dispatchConfiguratorChange(variant) {
      this.dispatchEvent(
        new CustomEvent('taplytap:configurator_change', {
          bubbles: true,
          detail: this.eventDetail(variant),
        })
      );
    }
  }

  if (!customElements.get('nfc-wholesale-configurator')) {
    customElements.define('nfc-wholesale-configurator', NfcWholesaleConfigurator);
  }
})();
